import { useState, useEffect, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import api from '../services/api';
import Button from '../components/Button';
import ServiceItemForm from '../components/ServiceItemForm';
import BeneficiaryPicker, { Beneficiary } from '../components/ticket/BeneficiaryPicker';
import { PRIORITIES } from '../utils/constants';
import { useDepartments } from '../hooks/useDepartments';
import type { Ticket, TicketItem } from '../types';

interface CustomField {
  id: string; fieldName: string; fieldLabel: string; fieldType: string;
  fieldOptions: string | null; isRequired: boolean; sortOrder: number;
}

const blank = (): Partial<TicketItem> => ({ category: '' });

const PURPOSE_MAX = 2000;

const fc = 'w-full text-sm border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-violet-600 focus:ring-1 focus:ring-violet-600 transition-all bg-white disabled:bg-gray-50';
const fcErr = 'w-full text-sm border border-red-400 rounded-lg px-3 py-2 outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500 transition-all bg-white disabled:bg-gray-50';
const lc = 'block text-xs font-medium text-gray-500 mb-1';

function FieldError({ msg }: { msg?: string }) {
  if (!msg) return null;
  return <span className="block text-xs text-red-600 mt-1">{msg}</span>;
}

// Splits the server's flat field-error map (e.g. "purpose", "items.0.aiToolName")
// into top-level field errors and per-item ({index: {field: message}}) errors, so
// each can be routed to the exact input that caused it.
function splitFieldErrors(fieldErrors: Record<string, string>) {
  const top: Record<string, string> = {};
  const perItem: Record<number, Record<string, string>> = {};
  for (const [key, msg] of Object.entries(fieldErrors)) {
    const m = key.match(/^items\.(\d+)\.(.+)$/);
    if (m) {
      const idx = Number(m[1]);
      (perItem[idx] ??= {})[m[2]] = msg;
    } else {
      top[key] = msg;
    }
  }
  return { top, perItem };
}

export default function NewTicket() {
  const { user }  = useAuth();
  const navigate  = useNavigate();
  const today     = new Date().toISOString().slice(0, 10);
  const { names: departmentNames } = useDepartments();

  // All hooks must be declared before any early return
  const [contact,    setContact]    = useState('');
  const [priority,   setPriority]   = useState('Medium');
  const [reqByDate,  setReqByDate]  = useState('');
  const [requestFor, setRequestFor] = useState<'myself' | 'someone else'>('myself');
  const [beneficiary, setBeneficiary] = useState<Beneficiary | null>(null);  // picked from master
  const [manualBene, setManualBene] = useState(false);                        // free-text fallback
  const [beneName,   setBeneName]   = useState('');
  const [beneUser,   setBeneUser]   = useState('');
  const [beneDept,   setBeneDept]   = useState('');
  const [beneTitle,  setBeneTitle]  = useState('');
  const [beneEmail,  setBeneEmail]  = useState('');
  const [purpose,    setPurpose]    = useState('');
  const [items,      setItems]      = useState<Partial<TicketItem>[]>([blank()]);
  const [acked,      setAcked]      = useState(false);
  const [saving,      setSaving]      = useState(false);
  // topError: a general "something's wrong, see below" banner — no error is ever
  // ONLY here; every validation failure also lands as a field-specific message via
  // fieldErrors/itemErrors, highlighting the exact input, per the field-level error
  // display used across this form.
  const [topError,    setTopError]    = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [itemErrors,  setItemErrors]  = useState<Record<number, Record<string, string>>>({});
  const [customFields, setCustomFields] = useState<CustomField[]>([]);
  const [fieldValues,  setFieldValues]  = useState<Record<string, string>>({});

  useEffect(() => {
    api.get<CustomField[]>('/admin/custom-fields?applies_to=ticket&active=true')
      .then(r => setCustomFields(r.data.sort((a, b) => a.sortOrder - b.sortOrder)))
      .catch(() => {});
  }, []);

  // Block employees and IT staff who have no line manager assigned
  const needsLM   = user?.role === 'employee' || user?.role === 'it_staff';
  const hasNoLM   = needsLM && !user?.managerId;

  if (hasNoLM) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate(-1)}
            className="text-gray-400 hover:text-gray-700 text-xl leading-none transition-colors">
            ←
          </button>
          <h1 className="text-lg font-bold text-[#010E23]">New IT Service Request</h1>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center space-y-3">
          <p className="text-2xl">🚫</p>
          <p className="text-sm font-semibold text-red-800">Cannot Submit Request</p>
          <p className="text-sm text-red-700 leading-relaxed max-w-md mx-auto">
            No Line Manager is assigned to your account. Please contact IT Admin to assign your
            Line Manager before submitting requests.
          </p>
          <p className="text-xs text-red-500">Contact: <a href="mailto:it-support@permus.io" className="underline">it-support@permus.io</a></p>
        </div>
      </div>
    );
  }

  const changeItem = (i: number) => (updated: Partial<TicketItem>) =>
    setItems(prev => prev.map((it, idx) => idx === i ? updated : it));

  const addItem    = () => setItems(prev => [...prev, blank()]);
  const removeItem = (i: number) => setItems(prev => prev.filter((_, idx) => idx !== i));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setTopError('');
    setFieldErrors({});
    setItemErrors({});

    const errs: Record<string, string> = {};
    if (!purpose.trim()) errs.purpose = 'Please fill in the business justification.';
    else if (purpose.trim().length < 5) errs.purpose = 'Business justification must be at least 5 characters.';
    else if (purpose.trim().length > PURPOSE_MAX) errs.purpose = `Business justification must be ${PURPOSE_MAX} characters or fewer.`;

    const valid = items.filter(it => it.category);
    if (!valid.length) errs.items = 'Add at least one service item with a category selected.';

    if (requestFor === 'someone else' && !beneficiary && !(manualBene && beneName.trim())) {
      errs.beneficiary = 'Select a beneficiary from the employee master, or enter their details manually.';
    }
    if (!acked) errs.acknowledgement = 'Please acknowledge the declaration before submitting.';

    const missingRequired = customFields.filter(f => f.isRequired && !fieldValues[f.id]?.trim());
    for (const f of missingRequired) errs[`cf_${f.id}`] = `${f.fieldLabel} is required.`;

    if (Object.keys(errs).length) {
      setFieldErrors(errs);
      setTopError('Please fix the highlighted fields below.');
      return;
    }

    setSaving(true);
    try {
      const cfValues = Object.entries(fieldValues)
        .filter(([, v]) => v?.trim())
        .map(([fieldId, value]) => ({ fieldId, value: value.trim() }));
      const payload: Record<string, any> = {
        purpose:       purpose.trim(),
        priority,

        contactNumber: contact     || undefined,
        requiredByDate: reqByDate  || undefined,
        requestFor,
        items:         valid,
        customFields:  cfValues.length ? cfValues : undefined,
      };
      if (requestFor === 'someone else') {
        if (beneficiary) {
          // Picked from the Employee Master — backend snapshots the details.
          payload.beneficiaryEmployeeId = beneficiary.id;
        } else {
          payload.beneficiaryName     = beneName              || undefined;
          payload.beneficiaryUsername = beneUser              || undefined;
          payload.beneficiaryDept     = beneDept              || undefined;
          payload.beneficiaryTitle    = beneTitle             || undefined;
          payload.beneficiaryEmail    = beneEmail             || undefined;
        }
      }
      const { data } = await api.post<Ticket>('/tickets', payload);
      navigate(`/tickets/${data.id}`, { state: { activeNav: '/tickets' } });
    } catch (err: any) {
      const serverFieldErrors = err?.response?.data?.fieldErrors as Record<string, string> | undefined;
      if (serverFieldErrors && Object.keys(serverFieldErrors).length) {
        const { top, perItem } = splitFieldErrors(serverFieldErrors);
        setFieldErrors(top);
        setItemErrors(perItem);
        setTopError('Please fix the highlighted fields below.');
      } else {
        setTopError(err?.response?.data?.message || 'Failed to submit request. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate(-1)}
          className="text-gray-400 hover:text-gray-700 text-xl leading-none transition-colors">
          ←
        </button>
        <h1 className="text-lg font-bold text-[#010E23]">New IT Service Request</h1>
      </div>

      <form onSubmit={submit} className="space-y-4">
        {topError && (
          <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-2.5">
            {topError}
          </div>
        )}

        {/* ── Section 1: Request Information ──────────────────────────────── */}
        <section className="bg-white rounded-xl border border-gray-200 p-5">
          <h2 className="text-sm font-semibold text-[#26148C] mb-4">
            Section 1 — Request Information
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={lc}>Request Date</label>
              <input className={`${fc} bg-gray-50 cursor-not-allowed`} value={today} readOnly />
            </div>
            <div>
              <label className={lc}>Contact Number</label>
              <input className={fc} value={contact} onChange={e => setContact(e.target.value)}
                placeholder="+966 5x xxx xxxx" />
            </div>
            <div>
              <label className={lc}>Priority <span className="text-red-500">*</span></label>
              <select className={fc} value={priority} onChange={e => setPriority(e.target.value)} required>
                {PRIORITIES.map(p => <option key={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className={lc}>Required By Date</label>
              <input type="date" className={fc} value={reqByDate}
                onChange={e => setReqByDate(e.target.value)} />
            </div>
            <div>
              <label className={lc}>Request For</label>
              <select className={fc} value={requestFor}
                onChange={e => setRequestFor(e.target.value as 'myself' | 'someone else')}>
                <option value="myself">Myself</option>
                <option value="someone else">Someone else</option>
              </select>
            </div>
          </div>

          {/* Beneficiary — picked from the Employee Master, with a manual fallback */}
          {requestFor === 'someone else' && (
            <div className="mt-4 bg-[#F4F4F8] rounded-lg p-4 border border-gray-100">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold text-gray-500">Beneficiary Details</p>
                <button
                  type="button"
                  className="text-xs font-medium text-violet-700 hover:underline"
                  onClick={() => {
                    setManualBene(m => !m);
                    setBeneficiary(null);
                  }}
                >
                  {manualBene ? 'Pick from employee master' : 'Enter manually instead'}
                </button>
              </div>

              {!manualBene ? (
                <BeneficiaryPicker value={beneficiary} onSelect={setBeneficiary} />
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={lc}>Full Name <span className="text-red-500">*</span></label>
                    <input className={fieldErrors.beneficiaryName ? fcErr : fc} value={beneName}
                      onChange={e => setBeneName(e.target.value)} />
                    <FieldError msg={fieldErrors.beneficiaryName} />
                  </div>
                  <div>
                    <label className={lc}>Username</label>
                    <input className={fc} value={beneUser} onChange={e => setBeneUser(e.target.value)} />
                  </div>
                  <div>
                    <label className={lc}>Department</label>
                    <select className={fc} value={beneDept} onChange={e => setBeneDept(e.target.value)}>
                      <option value="">Select…</option>
                      {departmentNames.map(d => <option key={d}>{d}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={lc}>Job Title</label>
                    <input className={fc} value={beneTitle} onChange={e => setBeneTitle(e.target.value)} />
                  </div>
                  <div className="col-span-2">
                    <label className={lc}>Email</label>
                    <input type="email" className={fc} value={beneEmail}
                      onChange={e => setBeneEmail(e.target.value)} />
                  </div>
                </div>
              )}
              <FieldError msg={fieldErrors.beneficiary} />
            </div>
          )}

          {/* Business justification */}
          <div className="mt-4">
            <label className={lc}>
              Business Justification / Purpose <span className="text-red-500">*</span>
            </label>
            <textarea className={`${fieldErrors.purpose ? fcErr : fc} resize-y`} rows={6} value={purpose}
              onChange={e => setPurpose(e.target.value)}
              placeholder="Why is this request needed? What business problem does it solve?"
              maxLength={PURPOSE_MAX} required />
            <div className="flex items-start justify-between mt-1">
              <FieldError msg={fieldErrors.purpose} />
              <span className="text-xs text-gray-400 shrink-0 ml-auto">{purpose.length} / {PURPOSE_MAX}</span>
            </div>
          </div>
        </section>

        {/* ── Section 2: Service Items ─────────────────────────────────────── */}
        <section className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-[#26148C]">
              Section 2 — Service Items
            </h2>
            <Button type="button" variant="secondary" size="sm" onClick={addItem}>
              + Add Item
            </Button>
          </div>
          <FieldError msg={fieldErrors.items} />
          <div className="space-y-3 mt-3">
            {items.map((item, i) => (
              <ServiceItemForm
                key={i}
                item={item}
                index={i}
                onChange={changeItem(i)}
                onRemove={() => removeItem(i)}
                canRemove={items.length > 1}
                errors={itemErrors[i]}
              />
            ))}
          </div>
        </section>

        {/* ── Section 3: Additional Information (custom fields) ───────────── */}
        {customFields.length > 0 && (
          <section className="bg-white rounded-xl border border-gray-200 p-5">
            <h2 className="text-sm font-semibold text-[#26148C] mb-4">
              Section 3 — Additional Information
            </h2>
            <div className="space-y-4">
              {customFields.map(f => {
                const val  = fieldValues[f.id] ?? '';
                const setVal = (v: string) => setFieldValues(p => ({ ...p, [f.id]: v }));
                const req = f.isRequired;
                const cfErr = fieldErrors[`cf_${f.id}`];
                const cfCls = cfErr ? fcErr : fc;
                return (
                  <div key={f.id}>
                    <label className={lc}>
                      {f.fieldLabel}{req && <span className="text-red-500 ml-0.5">*</span>}
                    </label>
                    {f.fieldType === 'textarea' && (
                      <textarea className={`${cfCls} resize-y`} rows={3} value={val}
                        onChange={e => setVal(e.target.value)} required={req} />
                    )}
                    {f.fieldType === 'select' && (() => {
                      let opts: string[] = [];
                      try { opts = JSON.parse(f.fieldOptions ?? '[]'); } catch {}
                      return (
                        <select className={cfCls} value={val} onChange={e => setVal(e.target.value)} required={req}>
                          <option value="">Select…</option>
                          {opts.map(o => <option key={o} value={o}>{o}</option>)}
                        </select>
                      );
                    })()}
                    {f.fieldType === 'checkbox' && (
                      <label className="flex items-center gap-2 cursor-pointer mt-1">
                        <input type="checkbox" className="rounded text-violet-600"
                          checked={val === 'true'}
                          onChange={e => setVal(e.target.checked ? 'true' : '')} />
                        <span className="text-sm text-gray-600">Yes</span>
                      </label>
                    )}
                    {f.fieldType === 'date' && (
                      <input type="date" className={cfCls} value={val}
                        onChange={e => setVal(e.target.value)} required={req} />
                    )}
                    {f.fieldType === 'number' && (
                      <input type="number" className={cfCls} value={val}
                        onChange={e => setVal(e.target.value)} required={req} />
                    )}
                    {(f.fieldType === 'text' || !['textarea','select','checkbox','date','number'].includes(f.fieldType)) && (
                      <input type="text" className={cfCls} value={val}
                        onChange={e => setVal(e.target.value)} required={req} />
                    )}
                    <FieldError msg={cfErr} />
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* ── Acknowledgment ───────────────────────────────────────────────── */}
        <section className="bg-white rounded-xl border border-gray-200 p-5">
          <label className="flex items-start gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-violet-600 focus:ring-violet-500"
              checked={acked}
              onChange={e => setAcked(e.target.checked)}
            />
            <span className="text-sm text-gray-700">
              I confirm this request is for legitimate business purposes and all information
              provided is accurate to the best of my knowledge.
            </span>
          </label>
          <FieldError msg={fieldErrors.acknowledgement} />
        </section>

        {/* ── Actions ──────────────────────────────────────────────────────── */}
        <div className="flex gap-3 pb-2">
          <Button type="submit" variant="navy" disabled={saving}>
            {saving ? 'Submitting…' : 'Submit Request'}
          </Button>
          <Button type="button" variant="ghost" onClick={() => navigate(-1)}>
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}
